import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';
import { Line } from 'react-chartjs-2';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPartner from '../Widget/OverlayPartner';
import NextAction from '../Widget/NextAction';

import ShareDrawer from '../../components/ShareDrawer';
import CssTransition from '../../components/Transition/CssTransition';
import EmailInterstitial from '../../components/EmailInterstitial/EmailInterstitialPopup';
import countdownAnimation from '../../components/Widget/CountdownAnimation.json';

import {
    Footer,
    Title as QuestionText,
    PeopleSigned as PeopleAnswered
} from '../DefaultStyles/Shared';
import { Container, Body } from '../DefaultStyles/Details';

import {
    Content,
    Details,
    Feedback,
    GraphContainer,
    SentimentLabel,
    SentimentLabelContainer,
    Slider,
    SliderContainer,
    SliderLogo,
    SliderSegment,
    SliderSegmentContainer,
    SliderThumb
} from '../../components/SentimentPoll/Components';

import {
    onShowEmailInterstitial,
    setOverlayPartnerVisible,
    widgetEngaged
} from '../Widget/actions';
import {
    onPollAnswered,
    onInteractWithSlider,
    sentimentPollAnswered,
    setPresetDistributionData
} from './actions';

import { CardTypes } from '../../utils/enums';
import { formatEmoji } from '../../utils/fillDynamicText';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { isLongText } from '../../components/Shared/commonFunctions';
import { isPortrait } from '../../utils/size';
import { sumDistributionRange, getFeedbackText } from '../../utils/sentimentPoll/helpers';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { copyFor, capitalization } from '../../copy/copy';

let lottie = {};
import('lottie-web').then(l => (lottie = l));

class SentimentPollDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sliderPosition: 50,
            isPaused: false
        };
    }

    static propTypes = {
        actionCompletedTime: PropTypes.func.isRequired,
        actionIdentifier: PropTypes.string.isRequired,
        activeAction: PropTypes.object,
        answerId: PropTypes.number,
        apiRequestData: PropTypes.object.isRequired,
        emailInterstitialVisibility: PropTypes.bool.isRequired,
        engaged: PropTypes.bool,
        graphHeight: PropTypes.number.isRequired,
        hasNextAction: PropTypes.bool.isRequired,
        isMultiActionsCard: PropTypes.bool.isRequired,
        leftPercentage: PropTypes.number,
        noOfAnswers: PropTypes.number.isRequired,
        onInteractWithSlider: PropTypes.func.isRequired,
        onPollAnswered: PropTypes.func.isRequired,
        onShowEmailInterstitial: PropTypes.func.isRequired,
        pollAnswered: PropTypes.bool.isRequired,
        questionText: PropTypes.string,
        resultDistribution: PropTypes.arrayOf(PropTypes.number).isRequired,
        rightPercentage: PropTypes.number,
        sentimentPollAnswered: PropTypes.func.isRequired,
        setOverlayPartnerVisible: PropTypes.func.isRequired,
        setPresetDistributionData: PropTypes.func.isRequired,
        showEmailInterstitial: PropTypes.bool.isRequired,
        widgetEngaged: PropTypes.func.isRequired
    };

    componentDidMount() {
        this.lottieCallback();
    }

    lottieCallback() {
        lottie.loadAnimation({
            container: this.sliderRef,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            animationData: countdownAnimation,
            progressiveLoad: false
        });
    }

    animateLottie(bool) {
        if (!bool) {
            this.setState({ isPaused: true });
        } else {
            this.sliderRef.innerHTML = ``;
            this.lottieCallback();
        }
    }

    onAnswerSentimentPoll = sentimentValue => {
        if (this.props.pollAnswered) {
            this.props.onPollAnswered(
                this.props.apiRequestData,
                this.props.actionIdentifier,
                this.props.actionCompletedTime(),
                this.props.questionText,
                sentimentValue / 100,
                this.props.isMultiActionsCard,
                this.props.answerId,
                this.props.pollAnswered,
                getFeedbackText(
                    this.props.activeAction,
                    this.props.resultDistribution,
                    sentimentValue / 100
                ),
                this.props.noOfAnswers + 1
            );
        } else {
            if (this.props.showEmailInterstitial) {
                this.props.onShowEmailInterstitial();
            } else {
                this.props.sentimentPollAnswered();
            }
            this.props.setPresetDistributionData(
                this.props.resultDistribution,
                sentimentValue / 100
            );
            this.props.onPollAnswered(
                this.props.apiRequestData,
                this.props.actionIdentifier,
                this.props.actionCompletedTime(),
                this.props.questionText,
                sentimentValue / 100,
                this.props.isMultiActionsCard,
                null,
                this.props.pollAnswered,
                getFeedbackText(
                    this.props.activeAction,
                    this.props.resultDistribution,
                    sentimentValue / 100
                ),
                this.props.noOfAnswers + 1
            );
        }
    };

    render() {
        const chartData = {
            labels: new Array((this.props.resultDistribution || []).length).fill(''),
            datasets: [
                {
                    backgroundColor: 'rgba(255, 36, 94, 0.2)',
                    borderCapStyle: 'round',
                    borderColor: 'rgba(255, 36, 94, 0)',
                    borderWidth: 0,
                    fill: true,
                    lineTension: 0.25,
                    pointBackgroundColor: '#fff',
                    pointBorderWidth: 3,
                    pointHitRadius: 10,
                    pointHoverRadius: 0,
                    pointRadius: 0,
                    data: this.props.resultDistribution
                }
            ]
        };
        const chartOptions = {
            tooltips: {
                enabled: false
            },
            legend: {
                display: false
            },
            layout: {
                padding: 0
            },
            responsive: true,
            animation: {
                duration: 800,
                delay: 1200,
                easing: 'easeInOutQuint'
            },
            scales: {
                xAxes: [
                    {
                        display: false
                    }
                ],
                yAxes: [
                    {
                        display: false,
                        ticks: {
                            beginAtZero: true
                        }
                    }
                ]
            }
        };

        return (
            <Container>
                <Overlay />
                <OverlayPublisher transitionType="right-to-left" />
                <OverlayPartner transitionType="right-to-left" />
                <Body>
                    <WidgetHeader />
                    <Content isSentimentPoll={true}>
                        <QuestionText
                            isLongText={isLongText(this.props.questionText, 40)}
                            isExtraLongText={isLongText(this.props.questionText, 60)}
                            dangerouslySetInnerHTML={{
                                __html: this.props.questionText
                            }}
                            onClick={e => {
                                e.preventDefault();
                                this.props.setOverlayPartnerVisible(true, e, false);
                            }}
                        />
                        {!this.props.pollAnswered ? (
                            <PeopleAnswered>
                                {this.props.noOfAnswers}{' '}
                                {copyFor(
                                    'miscellaneous',
                                    'numAnswered',
                                    'sentimentpoll',
                                    capitalization.all
                                )}
                            </PeopleAnswered>
                        ) : (
                            <Feedback pollAnswered={this.props.pollAnswered}>
                                {this.props.noOfAnswers}{' '}
                                {copyFor('miscellaneous', 'numAnswered', 'sentimentpoll')}
                                {'. '}
                                {getFeedbackText(
                                    this.props.activeAction,
                                    this.props.resultDistribution,
                                    this.state.sliderPosition / 100
                                )}
                            </Feedback>
                        )}
                        <Details
                            pollAnswered={this.props.pollAnswered}
                            hasNextAction={this.props.hasNextAction}
                        >
                            <GraphContainer
                                hasNextAction={this.props.hasNextAction}
                                maxSize={
                                    isPortrait()
                                        ? this.props.hasNextAction
                                            ? 0.75
                                            : 0.85
                                        : this.props.hasNextAction
                                            ? 0.5 // eslint-disable-line
                                            : 0.65 // eslint-disable-line
                                }
                                pollAnswered={this.props.pollAnswered}
                            >
                                {this.props.pollAnswered && (
                                    <Line
                                        height={this.props.graphHeight}
                                        options={chartOptions}
                                        data={chartData}
                                    />
                                )}
                            </GraphContainer>
                            <SliderContainer pollAnswered={this.props.pollAnswered}>
                                <SliderSegmentContainer
                                    pollAnswered={this.props.pollAnswered}
                                >
                                    <SliderSegment width={50} />
                                    <SliderSegment width={50} />
                                </SliderSegmentContainer>
                                <Slider
                                    isPaused={this.state.isPaused}
                                    onChange={e => {
                                        this.setState({
                                            sliderPosition: e.target.value
                                        });
                                    }}
                                    onTouchStart={() => {
                                        this.props.widgetEngaged(this.props.engaged);
                                        this.props.onInteractWithSlider({
                                            actionIdentifier: this.props.actionIdentifier,
                                            isActionInitiated: true
                                        });
                                        this.animateLottie(false);
                                    }}
                                    onMouseDown={() => {
                                        this.props.widgetEngaged(this.props.engaged);
                                        this.props.onInteractWithSlider({
                                            actionIdentifier: this.props.actionIdentifier,
                                            isActionInitiated: true
                                        });
                                        this.animateLottie(false);
                                    }}
                                    onTouchEnd={e => {
                                        this.props.onInteractWithSlider({
                                            isActionInitiated: false
                                        });
                                        this.onAnswerSentimentPoll(e.target.value);
                                    }}
                                    onMouseUp={e => {
                                        this.props.onInteractWithSlider({
                                            isActionInitiated: false
                                        });
                                        this.onAnswerSentimentPoll(e.target.value);
                                    }}
                                    type="range"
                                />
                                <SliderThumb
                                    isPaused={this.state.isPaused}
                                    position={this.state.sliderPosition}
                                    innerRef={i => (this.sliderRef = i)}
                                >
                                    <SliderLogo
                                        isPaused={this.state.isPaused}
                                        pollAnswered={this.props.pollAnswered}
                                    />
                                </SliderThumb>
                            </SliderContainer>
                            <SentimentLabelContainer>
                                <SentimentLabel
                                    pollAnswered={this.props.pollAnswered}
                                    hasNextAction={this.props.hasNextAction}
                                >
                                    <h5>
                                        {this.props.pollAnswered &&
                                            `${this.props.leftPercentage}%`}
                                    </h5>
                                    <h4
                                        dangerouslySetInnerHTML={{
                                            __html: formatEmoji(
                                                this.props.activeAction.leftChoice
                                            )
                                        }}
                                    />
                                </SentimentLabel>
                                <SentimentLabel
                                    pollAnswered={this.props.pollAnswered}
                                    hasNextAction={this.props.hasNextAction}
                                >
                                    <h5>
                                        {this.props.pollAnswered &&
                                            `${this.props.rightPercentage}%`}
                                    </h5>
                                    <h4
                                        dangerouslySetInnerHTML={{
                                            __html: formatEmoji(
                                                this.props.activeAction.rightChoice
                                            )
                                        }}
                                    />
                                </SentimentLabel>
                            </SentimentLabelContainer>
                        </Details>
                    </Content>
                    {this.props.pollAnswered &&
                        this.props.hasNextAction &&
                        !this.props.emailInterstitialVisibility && <NextAction />}
                    <Footer>
                        <ShareDrawer
                            fullWidth
                            backgroundColor={this.props.pollAnswered ? null : '#ffffff'}
                            newsletterSignUpVisible={true}
                        />
                    </Footer>
                </Body>
                <TransitionGroup>
                    {this.props.emailInterstitialVisibility && (
                        <CssTransition
                            key="alert"
                            in={this.props.onShowEmailInterstitial}
                        >
                            <EmailInterstitial skipSuccessNavigation={true} />
                        </CssTransition>
                    )}
                </TransitionGroup>
            </Container>
        );
    }
}

export default connect(
    state => {
        const hasNextAction =
            getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            ) !== null;

        return {
            actionCompletedTime: getActionCompletedTime(state, 'sentimentPoll'),
            actionIdentifier: state.widget.activeAction.identifier,
            activeAction: state.widget.activeAction,
            answerId: state.sentimentPoll.pollAnswered
                ? state.sentimentPoll.answerId
                : null,
            apiRequestData: state.widget.apiRequestData,
            emailInterstitialVisibility: state.widget.showEmailInterstitial || false,
            engaged: state.widget.engaged,
            hasNextAction,
            graphHeight: hasNextAction
                ? isPortrait()
                    ? 210
                    : 50
                : isPortrait()
                ? 235
                : 80,
            isLastActionInWidget:
                state.widget.cards.length === state.widget.activeAction.order,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            leftPercentage:
                state.sentimentPoll.resultDistribution.length > 0
                    ? Math.round(
                          (sumDistributionRange(
                              state.sentimentPoll.resultDistribution,
                              0,
                              6
                          ) /
                              sumDistributionRange(
                                  state.sentimentPoll.resultDistribution,
                                  0,
                                  12
                              )) *
                              100
                      )
                    : 0,
            noOfAnswers: state.widget.activeAction.noOfAnswers || 53,
            pollAnswered: state.sentimentPoll.pollAnswered,
            questionText:
                state.widget.cards[state.widget.activeAction.order - 1].heading ||
                state.widget.activeAction.name,
            resultDistribution:
                state.sentimentPoll.resultDistribution.length > 0
                    ? state.sentimentPoll.resultDistribution
                    : state.widget.activeAction.resultDistribution,
            rightPercentage:
                state.sentimentPoll.resultDistribution.length > 0
                    ? Math.round(
                          (sumDistributionRange(
                              state.sentimentPoll.resultDistribution,
                              6,
                              12
                          ) /
                              sumDistributionRange(
                                  state.sentimentPoll.resultDistribution,
                                  0,
                                  12
                              )) *
                              100
                      )
                    : 0,
            showEmailInterstitial: state.widget.activeAction.collectEmail > 0,
            widgetId: state.widget.widgetRequestId
        };
    },
    {
        onInteractWithSlider,
        onPollAnswered,
        onShowEmailInterstitial,
        sentimentPollAnswered,
        setOverlayPartnerVisible,
        setPresetDistributionData,
        widgetEngaged
    }
)(SentimentPollDetails);



// WEBPACK FOOTER //
// ./src/containers/SentimentPoll/SentimentPollDetails.jsx